import { ChangeDetectionStrategy, Component, Inject, Injectable } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, first, switchMap } from 'rxjs/operators';

export interface ConfirmButton {
  name: string;
  handler?: (e?: MouseEvent) => void;
  color: ThemePalette;
}

export interface AskConfig {
  message: string | { content: string; args?: _.Dictionary<string | number> };
  buttons: ConfirmButton[];
}

export const Buttons = {
  warn: (name: string, handler?: (e?: MouseEvent) => void): ConfirmButton => ({ name, handler, color: 'warn' }),
  primary: (name: string, handler?: (e?: MouseEvent) => void): ConfirmButton => ({ name, handler, color: 'primary' }),
};

@Injectable({
  providedIn: 'root',
})
export class ConfirmActionService {
  constructor(private dialog: MatDialog, private translateService: TranslateService) { }

  show(message: string, buttons: ConfirmButton[]): void {
    this.dialog.open(ConfirmActionModalComponent, {
      data: { message, buttons },
    });
  }

  ask(config: AskConfig): void {
    const message = config.message;
    const ready: Observable<unknown> = this.translateService.currentLang ? of(null) : this.translateService.onLangChange;
    ready.pipe(
      first(),
      switchMap(() => typeof message === 'string'
        ? this.translateService.get(message)
        : this.translateService.get(message.content, message.args)),
      first(),
      catchError((error: unknown) => {
        console.error('Unable to load confirmation translations:', error);
        return EMPTY;
      }),
    ).subscribe(translated => this.show(translated, config.buttons));
  }

  info(message: string): void {
    this.dialog.open(ConfirmActionModalComponent, {
      data: { message, buttons: [Buttons.primary('COMMON.ok')] },
    });
  }
}

@Component({
  selector: 'app-confirm-action',
  template: `
    <mat-dialog-content>
      <p>{{ data.message }}</p>
    </mat-dialog-content>
    <app-button-group>
      @for (button of data.buttons; track button) {
        <button mat-raised-button [color]="button.color"
        (click)="handleClick(button, $event)">{{ button.name | translate }}</button>
      }
    </app-button-group>
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConfirmActionModalComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { message: string; buttons: ConfirmButton[] },
    public dialogRef: MatDialogRef<ConfirmActionModalComponent>,
  ) {}

  handleClick(button: ConfirmButton, e: MouseEvent): void {
    this.dialogRef.close();

    if (button.handler) {
      return button.handler(e);
    }
  }
}
